import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Download, ChevronUp, ChevronDown, ChevronsUpDown, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const PROGRESS_OPTIONS = ['新學員', '學習中', '準備考試', '已完成'];

// Merge managed categories with current value (for legacy records that have a value
// not in the managed list — show it so user can keep or pick a managed type).
const mergeWithCurrent = (managed, currentValue) => {
  const list = managed.map((m) => m.name);
  if (currentValue && !list.includes(currentValue)) {
    return [...list, currentValue];
  }
  return list;
};

const emptyForm = {
  name: '',
  phone: '',
  email: '',
  course_type: '',
  instructor: '',
  progress: '新學員',
  notes: '',
  exam_date: '',
  exam_venue: '',
  student_category: '',
  source: '',
};

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [instructorsList, setInstructorsList] = useState([]);
  const [managedCategories, setManagedCategories] = useState({
    course_type: [],
    student_category: [],
    source: [],
    exam_venue: [],
  });
  const [formData, setFormData] = useState(emptyForm);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ course_type: '', instructor: '', student_category: '', exam_venue: '' });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filteredClients = clients.filter((c) => {
    const q = search.toLowerCase();
    if (q && !['name','phone','email','notes'].some(f => (c[f] || '').toLowerCase().includes(q))) return false;
    if (filters.course_type && c.course_type !== filters.course_type) return false;
    if (filters.instructor && c.instructor !== filters.instructor) return false;
    if (filters.student_category && c.student_category !== filters.student_category) return false;
    if (filters.exam_venue && c.exam_venue !== filters.exam_venue) return false;
    return true;
  });

  const sortedClients = [...filteredClients].sort((a, b) => {
    const av = a[sortField] || '';
    const bv = b[sortField] || '';
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const hasActiveFilters = search || Object.values(filters).some(v => v);
  const clearFilters = () => { setSearch(''); setFilters({ course_type: '', instructor: '', student_category: '', exam_venue: '' }); };

  const exportCsv = () => {
    const headers = ['姓名', '電話', '電郵', '課程類別', '導師', '學員類別', '來源', '考試日期', '考試場地', '備註'];
    const rows = sortedClients.map(c => [
      c.name, c.phone, c.email || '', c.course_type || '', c.instructor || '',
      c.student_category || '', c.source || '', c.exam_date || '', c.exam_venue || '', c.notes || ''
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `學員資料_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronsUpDown className="inline h-3 w-3 ml-1 text-gray-400" />;
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-1 text-primary" />
      : <ChevronDown className="inline h-3 w-3 ml-1 text-primary" />;
  };

  useEffect(() => {
    fetchClients();
    fetchInstructors();
    fetchManagedCategories();
  }, []);

  const fetchInstructors = async () => {
    try {
      const response = await axios.get(`${API_URL}/instructors`);
      setInstructorsList(response.data);
    } catch (error) {
      console.error('Failed to fetch instructors:', error);
    }
  };

  const fetchManagedCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      const grouped = { course_type: [], student_category: [], source: [], exam_venue: [] };
      for (const cat of response.data) {
        if (grouped[cat.type]) grouped[cat.type].push(cat);
      }
      setManagedCategories(grouped);
    } catch (error) {
      console.error('Failed to fetch managed categories:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.exam_date) payload.exam_date = null;
      if (!payload.email) payload.email = null;
      if (editingClient) {
        await axios.put(`${API_URL}/clients/${editingClient.id}`, payload);
        toast.success('更新成功');
      } else {
        await axios.post(`${API_URL}/clients`, payload);
        toast.success('新增成功');
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error('操作失敗');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/clients/${deleteTarget}`);
      toast.success('刪除成功');
      fetchClients();
    } catch (error) {
      toast.error('刪除失敗');
    } finally {
      setDeleteTarget(null);
    }
  };

  const openDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name || '',
        phone: client.phone || '',
        email: client.email || '',
        course_type: client.course_type || '',
        instructor: client.instructor || '',
        progress: client.progress || '新學員',
        notes: client.notes || '',
        exam_date: client.exam_date || '',
        exam_venue: client.exam_venue || '',
        student_category: client.student_category || '',
        source: client.source || '',
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData(emptyForm);
  };

  const courseTypeOptions = mergeWithCurrent(managedCategories.course_type, formData.course_type);
  const categoryOptions = mergeWithCurrent(managedCategories.student_category, formData.student_category);
  const sourceOptions = mergeWithCurrent(managedCategories.source, formData.source);
  const venueOptions = mergeWithCurrent(managedCategories.exam_venue, formData.exam_venue);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <h1 className="text-3xl font-bold">學員管理</h1>
            <p className="text-muted-foreground">管理學員資料和學習進度</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportCsv}>
              <Download className="mr-2 h-4 w-4" /> 匯出 CSV
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button onClick={() => openDialog()} data-testid="add-client-button">
                <Plus className="mr-2 h-4 w-4" /> 新增學員
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingClient ? '編輯學員' : '新增學員'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>姓名 *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>電話 *</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>電郵</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>學員來源</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.source}
                      onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    >
                      <option value="">請選擇學員來源...</option>
                      {sourceOptions.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>課程類別</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.course_type}
                      onChange={(e) => setFormData({ ...formData, course_type: e.target.value })}
                    >
                      <option value="">請選擇課程類別...</option>
                      {courseTypeOptions.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>導師</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.instructor}
                      onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                    >
                      <option value="">請選擇導師...</option>
                      {instructorsList.map((inst) => (
                        <option key={inst.id} value={inst.name}>{inst.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>學員類別</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.student_category}
                      onChange={(e) => setFormData({ ...formData, student_category: e.target.value })}
                    >
                      <option value="">請選擇學員類別...</option>
                      {categoryOptions.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>考試日期</Label>
                    <Input
                      type="date"
                      value={formData.exam_date}
                      onChange={(e) => setFormData({ ...formData, exam_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>考試場地</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.exam_venue}
                      onChange={(e) => setFormData({ ...formData, exam_venue: e.target.value })}
                    >
                      <option value="">請選擇考試場地...</option>
                      {venueOptions.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <Label>備註</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit">儲存</Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    取消
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Search + Filter bar */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="搜尋姓名、電話、電郵..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <select className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={filters.course_type} onChange={(e) => setFilters({ ...filters, course_type: e.target.value })}>
            <option value="">所有課程</option>
            {managedCategories.course_type.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          <select className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={filters.instructor} onChange={(e) => setFilters({ ...filters, instructor: e.target.value })}>
            <option value="">所有導師</option>
            {instructorsList.map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
          </select>
          <select className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={filters.student_category} onChange={(e) => setFilters({ ...filters, student_category: e.target.value })}>
            <option value="">所有類別</option>
            {managedCategories.student_category.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          <select className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={filters.exam_venue} onChange={(e) => setFilters({ ...filters, exam_venue: e.target.value })}>
            <option value="">所有場地</option>
            {managedCategories.exam_venue.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4 mr-1" /> 清除
            </Button>
          )}
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {sortedClients.length} / {clients.length} 位學員
          </span>
        </div>

        {loading ? (
          <div className="text-center py-12">載入中...</div>
        ) : (
          <div className="bg-white rounded-lg border overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm">
              <thead className="border-b bg-gray-50">
                <tr>
                  {[
                    { label: '姓名', field: 'name', cls: '' },
                    { label: '電話', field: 'phone', cls: 'hidden md:table-cell' },
                    { label: '課程類別', field: 'course_type', cls: '' },
                    { label: '導師', field: 'instructor', cls: 'hidden md:table-cell' },
                    { label: '類別', field: 'student_category', cls: 'hidden lg:table-cell' },
                    { label: '來源', field: 'source', cls: 'hidden xl:table-cell' },
                    { label: '考試日期', field: 'exam_date', cls: 'hidden lg:table-cell' },
                    { label: '場地', field: 'exam_venue', cls: 'hidden xl:table-cell' },
                  ].map(({ label, field, cls }) => (
                    <th key={field} className={`text-left p-3 cursor-pointer select-none hover:bg-gray-100 ${cls}`} onClick={() => handleSort(field)}>
                      {label}<SortIcon field={field} />
                    </th>
                  ))}
                  <th className="text-right p-3">操作</th>
                </tr>
              </thead>
              <tbody>
                {sortedClients.map((client) => (
                  <tr key={client.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="p-3 font-medium">{client.name}</td>
                    <td className="p-3 hidden md:table-cell">{client.phone}</td>
                    <td className="p-3">{client.course_type || '-'}</td>
                    <td className="p-3 hidden md:table-cell">{client.instructor || '-'}</td>
                    <td className="p-3 hidden lg:table-cell">{client.student_category || '-'}</td>
                    <td className="p-3 hidden xl:table-cell">{client.source || '-'}</td>
                    <td className="p-3 hidden lg:table-cell">{client.exam_date || '-'}</td>
                    <td className="p-3 hidden xl:table-cell">{client.exam_venue || '-'}</td>
                    <td className="p-3 text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => openDialog(client)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => setDeleteTarget(client.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除此學員？</AlertDialogTitle>
            <AlertDialogDescription>此操作不可撤銷，學員資料將永久刪除。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminClients;
